import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Trophy, Star, Shield, Zap, Target, Award, Lock, CheckCircle, ChevronRight, Flame, Crown, Eye } from 'lucide-react';

const BADGE_CATEGORIES = [
  {
    id: 'protection',
    title: 'Protection',
    color: '#00F0FF',
    badges: [
      { id: 'first_wrap', name: 'First Wrap', desc: 'Wrap your first asset with .VIG Protocol', icon: Shield, unlocked: true, xp: 100 },
      { id: 'vault_5', name: 'Vault Builder', desc: 'Connect 3+ social platforms to the Vault', icon: Lock, unlocked: true, xp: 200 },
      { id: 'wrap_10', name: 'Armored Up', desc: 'Wrap 10 assets with .VIG Protocol', icon: Shield, unlocked: false, xp: 500, progress: 3, target: 10 },
      { id: 'wrap_100', name: 'Fort Knox', desc: 'Wrap 100 assets with .VIG Protocol', icon: Crown, unlocked: false, xp: 2000, progress: 3, target: 100 },
    ]
  },
  {
    id: 'enforcement',
    title: 'Enforcement',
    color: '#FF00E5',
    badges: [
      { id: 'first_scan', name: 'Eagle Eye', desc: 'Complete your first content scan', icon: Eye, unlocked: true, xp: 100 },
      { id: 'first_settlement', name: 'First Blood', desc: 'Settle your first DMCA case', icon: Target, unlocked: true, xp: 300 },
      { id: 'settle_5', name: 'Velvet Hammer', desc: 'Settle 5 enforcement cases', icon: Zap, unlocked: false, xp: 1000, progress: 2, target: 5 },
      { id: 'recover_10k', name: 'Money Machine', desc: 'Recover $10,000+ in settlements', icon: Trophy, unlocked: true, xp: 2500 },
    ]
  },
  {
    id: 'marketplace',
    title: 'Marketplace',
    color: '#8B5CF6',
    badges: [
      { id: 'first_list', name: 'Open for Business', desc: 'List your first asset on the marketplace', icon: Star, unlocked: false, xp: 150, progress: 0, target: 1 },
      { id: 'first_sale', name: 'First Sale', desc: 'Make your first marketplace sale', icon: Award, unlocked: false, xp: 300, progress: 0, target: 1 },
      { id: 'sales_50', name: 'Top Seller', desc: 'Sell 50 marketplace licenses', icon: Flame, unlocked: false, xp: 2000, progress: 0, target: 50 },
      { id: 'revenue_5k', name: 'Marketplace Mogul', desc: 'Earn $5,000+ in marketplace revenue', icon: Crown, unlocked: false, xp: 5000, progress: 0, target: 5000 },
    ]
  },
  {
    id: 'community',
    title: 'Community',
    color: '#F59E0B',
    badges: [
      { id: 'profile_complete', name: 'Identity Verified', desc: 'Complete your creator profile', icon: CheckCircle, unlocked: true, xp: 50 },
      { id: 'refer_1', name: 'Ambassador', desc: 'Refer another creator to the platform', icon: Star, unlocked: false, xp: 500, progress: 0, target: 1 },
      { id: 'streak_7', name: '7-Day Streak', desc: 'Log in 7 consecutive days', icon: Flame, unlocked: false, xp: 300, progress: 4, target: 7 },
      { id: 'streak_30', name: '30-Day Warrior', desc: 'Log in 30 consecutive days', icon: Crown, unlocked: false, xp: 1500, progress: 4, target: 30 },
    ]
  },
];

const MILESTONES = [
  { level: 1, title: 'Rookie', xpNeeded: 0, color: '#6B7280' },
  { level: 2, title: 'Scout', xpNeeded: 500, color: '#00F0FF' },
  { level: 3, title: 'Guardian', xpNeeded: 1500, color: '#8B5CF6' },
  { level: 4, title: 'Enforcer', xpNeeded: 3500, color: '#FF00E5' },
  { level: 5, title: 'Commander', xpNeeded: 7000, color: '#F59E0B' },
  { level: 6, title: 'Overlord', xpNeeded: 15000, color: '#DC2626' },
];

export default function GamificationPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    if (!user) navigate('/auth');
  }, [user, navigate]);

  // Calculate total XP from unlocked badges
  const totalXP = BADGE_CATEGORIES.flatMap(c => c.badges).filter(b => b.unlocked).reduce((sum, b) => sum + b.xp, 0);
  const unlockedCount = BADGE_CATEGORIES.flatMap(c => c.badges).filter(b => b.unlocked).length;
  const totalBadges = BADGE_CATEGORIES.flatMap(c => c.badges).length;

  // Determine current level
  const currentMilestone = [...MILESTONES].reverse().find(m => totalXP >= m.xpNeeded) || MILESTONES[0];
  const nextMilestone = MILESTONES.find(m => m.xpNeeded > totalXP);
  const progressToNext = nextMilestone
    ? ((totalXP - currentMilestone.xpNeeded) / (nextMilestone.xpNeeded - currentMilestone.xpNeeded)) * 100
    : 100;

  const filteredCategories = selectedCategory === 'all'
    ? BADGE_CATEGORIES
    : BADGE_CATEGORIES.filter(c => c.id === selectedCategory);

  return (
    <div className="min-h-screen bg-texture" data-testid="gamification-page">
      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8 animate-fade-in-up">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-4"
            style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)' }}>
            <Trophy size={14} className="text-[--ch-warning]" />
            <span className="font-label text-[--ch-warning]">ACHIEVEMENTS</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-black" data-testid="gamification-title">
            <span className="text-white">Creator </span><span className="neon-cyan">Achievements</span>
          </h1>
          <p className="text-sm text-[--ch-text-secondary] mt-1">
            Earn badges, gain XP, and level up your creator rank.
          </p>
        </div>

        {/* Level Card */}
        <div className="ch-card p-6 mb-8 relative overflow-hidden animate-fade-in-up stagger-1" data-testid="level-card">
          <div className="absolute top-0 right-0 w-40 h-40 rounded-full opacity-[0.05] blur-[50px]"
            style={{ background: currentMilestone.color }} />
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
            <div className="w-16 h-16 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ background: `${currentMilestone.color}15`, border: `2px solid ${currentMilestone.color}40` }}>
              <span className="text-2xl font-black" style={{ color: currentMilestone.color, fontFamily: 'Orbitron' }}>
                {currentMilestone.level}
              </span>
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h2 className="text-xl font-black" style={{ fontFamily: 'Orbitron', color: currentMilestone.color }}>
                  {currentMilestone.title}
                </h2>
                <span className="text-xs px-2 py-0.5 rounded font-bold"
                  style={{ background: `${currentMilestone.color}15`, color: currentMilestone.color }}>
                  RANK {currentMilestone.level}
                </span>
              </div>
              <p className="text-sm text-[--ch-text-secondary] mb-3">
                {totalXP.toLocaleString()} XP earned | {unlockedCount}/{totalBadges} badges unlocked
              </p>
              {nextMilestone && (
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] text-[--ch-text-secondary]">Next: {nextMilestone.title}</span>
                    <span className="text-[10px] font-mono" style={{ color: nextMilestone.color }}>
                      {totalXP}/{nextMilestone.xpNeeded} XP
                    </span>
                  </div>
                  <div className="h-2 bg-[--ch-bg-secondary] rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all duration-700"
                      style={{
                        width: `${progressToNext}%`,
                        background: `linear-gradient(90deg, ${currentMilestone.color}, ${nextMilestone.color})`,
                        boxShadow: `0 0 10px ${currentMilestone.color}60`,
                      }} />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Milestone track */}
          <div className="flex items-center mt-6 gap-0">
            {MILESTONES.map((m, i) => (
              <React.Fragment key={m.level}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 border-2 transition-all ${
                  totalXP >= m.xpNeeded ? 'scale-100' : 'scale-90 opacity-40'
                }`} style={{
                  borderColor: totalXP >= m.xpNeeded ? m.color : '#333',
                  background: totalXP >= m.xpNeeded ? `${m.color}20` : 'transparent',
                }}>
                  <span className="text-[10px] font-bold" style={{ color: totalXP >= m.xpNeeded ? m.color : '#555' }}>
                    {m.level}
                  </span>
                </div>
                {i < MILESTONES.length - 1 && (
                  <div className="flex-1 h-0.5 mx-1" style={{
                    background: totalXP >= MILESTONES[i+1].xpNeeded
                      ? `linear-gradient(90deg, ${m.color}, ${MILESTONES[i+1].color})`
                      : '#222'
                  }} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Category Filter */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-1 animate-fade-in-up stagger-2">
          {[{ id: 'all', label: 'All Badges', color: '#fff' }, ...BADGE_CATEGORIES.map(c => ({ id: c.id, label: c.title, color: c.color }))].map(f => (
            <button key={f.id} onClick={() => setSelectedCategory(f.id)}
              data-testid={`badge-filter-${f.id}`}
              className={`px-4 py-2 rounded-lg text-xs font-bold whitespace-nowrap transition-all ${
                selectedCategory === f.id
                  ? 'border'
                  : 'bg-[--ch-bg-card] text-[--ch-text-secondary] border border-[--ch-border] hover:border-gray-500'
              }`}
              style={selectedCategory === f.id ? { background: `${f.color}10`, borderColor: `${f.color}40`, color: f.color } : {}}>
              {f.label}
            </button>
          ))}
        </div>

        {/* Badge Grid */}
        {filteredCategories.map(category => (
          <div key={category.id} className="mb-8 animate-fade-in-up">
            <h3 className="text-sm font-bold mb-4 flex items-center gap-2" style={{ color: category.color }}>
              <div className="w-1.5 h-4 rounded" style={{ background: category.color }} />
              {category.title}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {category.badges.map(badge => {
                const Icon = badge.icon;
                return (
                  <div key={badge.id} data-testid={`badge-${badge.id}`}
                    className={`ch-card p-4 transition-all ${badge.unlocked ? 'border-[rgba(34,197,94,0.2)]' : 'opacity-60'}`}>
                    <div className="flex items-start gap-3">
                      <div className={`w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        badge.unlocked ? '' : 'grayscale'
                      }`} style={{
                        background: badge.unlocked ? `${category.color}15` : 'rgba(60,60,60,0.3)',
                        border: `1px solid ${badge.unlocked ? `${category.color}30` : '#333'}`,
                      }}>
                        {badge.unlocked ? (
                          <Icon size={20} style={{ color: category.color }} />
                        ) : (
                          <Lock size={16} className="text-gray-600" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <h4 className="text-sm font-bold">{badge.name}</h4>
                          {badge.unlocked && <CheckCircle size={12} className="text-[--ch-success]" />}
                        </div>
                        <p className="text-xs text-[--ch-text-secondary]">{badge.desc}</p>
                        {!badge.unlocked && badge.progress !== undefined && (
                          <div className="mt-2">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-[10px] text-[--ch-text-secondary]">
                                {badge.progress}/{badge.target}
                              </span>
                              <span className="text-[10px] font-bold" style={{ color: category.color }}>
                                {Math.round((badge.progress / badge.target) * 100)}%
                              </span>
                            </div>
                            <div className="h-1.5 bg-[--ch-bg-secondary] rounded-full overflow-hidden">
                              <div className="h-full rounded-full" style={{
                                width: `${(badge.progress / badge.target) * 100}%`,
                                background: category.color,
                                opacity: 0.7,
                              }} />
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="text-right flex-shrink-0">
                        <span className="text-xs font-bold" style={{ color: badge.unlocked ? '#22C55E' : '#555' }}>
                          +{badge.xp} XP
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
